'use client';

import { TextareaAutosize } from '@mui/material';
import { css } from '@pt-group-fe/styled-system/css';
import { Center, HStack, Stack } from '@pt-group-fe/styled-system/jsx';
import { Bug } from 'lucide-react';
import Link from 'next/link';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { useLanguage, useLogger } from '@lib/hooks';
import { useTranslation } from '@lib/services/i18n/client';
import { Button } from '@lib/ui/button';
import { Text } from '@lib/ui/text';
import { extractErrorMessage } from '@lib/utils/extractErrorMsg';

const stError = css({
  fontFamily: 'mono',
  fontSize: 'sm',
  lineHeight: '1.2',
  bg: 'red.50',
  color: 'red.800',
  p: 3,
  resize: 'none',
  border: '1px solid',
  rounded: 'md',
  borderColor: 'red.700'
});

interface ClientErrorBoundaryProps extends React.PropsWithChildren {
  fallback?: ({ error, resetErrorBoundary }: FallbackProps) => React.ReactNode;
}

const ClientErrorContent: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const { t } = useTranslation();
  const lng = useLanguage();
  const err = extractErrorMessage(error);

  return (
    <Center minH={{ lg: 'calc(100dvh - 300px)' }}>
      <Stack py={8} gap={6} w="full" maxW={800}>
        <Stack
          border="1px solid {colors.bg.divider}"
          bg={{ base: 'white', _dark: 'white/10' }}
          p={4}
          rounded={8}
          gap={4}
          shadow="xs"
        >
          <HStack
            color={{ base: 'primary', _dark: 'primary.light' }}
            gap={2}
            justifyContent="space-between"
          >
            <Text variant="h4" as="h1">
              {t('errorBoundary.title', 'Something went wrong')}
            </Text>
            <Bug size={20} />
          </HStack>
          <Text variant="block-sm">
            {t(
              'errorBoundary.description',
              'An unexpected error occurred. Please try again later.'
            )}
          </Text>
          <HStack mt={2}>
            <Button onClick={resetErrorBoundary}>{t('errorBoundary.reload', 'Reload')}</Button>
            <Link href={`/${lng}`}>
              <Button>{t('errorBoundary.backToHome', 'Back to home')}</Button>
            </Link>
          </HStack>
        </Stack>

        {err && err !== '' && (
          <Stack
            border="1px solid {colors.bg.divider}"
            bg={{ base: 'white', _dark: 'white/10' }}
            p={4}
            rounded={8}
            shadow="xs"
          >
            <Text variant="block-sm">{t('errorBoundary.error', 'Error details:')}</Text>
            <TextareaAutosize className={stError} value={err} />
          </Stack>
        )}
      </Stack>
    </Center>
  );
};

const ClientErrorBoundary: React.FC<ClientErrorBoundaryProps> = ({ children, fallback }) => {
  const log = useLogger('ClientErrorBoundary');

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (fallback) {
          return fallback({ error, resetErrorBoundary });
        }

        return <ClientErrorContent error={error} resetErrorBoundary={resetErrorBoundary} />;
      }}
      onError={(error, info) => {
        // logErrorToMyService can be called here
        log.error('Error occurred', error, info);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export { ClientErrorBoundary, ClientErrorContent };
